import { cloneDeep, isEqual } from 'lodash-es';
import { IFurniture } from '../data/model/furniture';
import { IPoint2d } from '../math/IPoint2d';
import { globalStores, TGlobalStores } from '../stores';
import { EEntityType, EOperateMode, EToolType, ISelected } from '../types';
import { Point2dUtils } from '../math/Point2dUtils';
import { EOperationType } from '../types/operation';
import { modelManager } from '../manager/entityManager';
import { getUniqueId } from '../utils';
import { MULTI_SELECT_TEMPORARY_ID } from '../constant/entity';
import { IBoxModelInfo, IModelInfo, IMultiModelData } from '../data/multiModel/multiModel';
import { getMultiModelBoxInfo, toBoxModelInfos } from '../data/multiModel/utils';
import { normalizeAngle } from '../math/utils';
import { IWindowData } from '../data/model/model';
import { EParamsKeys } from '../types/editParamType';
import { getModifyDoorWindowData } from '../utils/editModelUtils';
import { SeparateWallData } from '../data/separateWall/SeparateWall';

export class MultiModelOperation {
  private _assemblyStore: TGlobalStores['assemblyStore'];
  private _modelStore: TGlobalStores['modelStore'];
  private _separateWallStore: TGlobalStores['separateWallStore'];
  private _editorStateStore: TGlobalStores['editorStateStore'];
  private _history: TGlobalStores['history'];

  constructor(gs: TGlobalStores) {
    this._assemblyStore = gs.assemblyStore;
    this._modelStore = gs.modelStore;
    this._separateWallStore = gs.separateWallStore;
    this._editorStateStore = gs.editorStateStore;
    this._history = gs.history;
  }

  private $history_modifyMultiModel(modelInfos: IModelInfo[]) {
    this._history.startOperation();
    for (const modelInfo of modelInfos) {
      if (modelInfo.type === EEntityType.Furniture) {
        this._modelStore.updateFurnitureData(modelInfo.id, {
          center: modelInfo.data.center,
          rotation: modelInfo.data.rotation
        });
      } else if (modelInfo.type === EEntityType.Assembly) {
        // 修改组合数据
        this._assemblyStore.updateAssemblyData(modelInfo.data.id, {
          center: modelInfo.data.center,
          rotation: modelInfo.data.rotation
        });

        for (const furnitureData of modelInfo.subModeldatas) {
          this._modelStore.updateFurnitureData(furnitureData.id, {
            center: furnitureData.center,
            rotation: furnitureData.rotation
          });
        }
      }
    }

    this._history.completeOperation(EOperationType.MultipleEntityModify);
  }

  private $history_copyMultiModel(modelInfos: IModelInfo[]) {
    this._history.startOperation();

    for (const modelInfo of modelInfos) {
      if (modelInfo.type === EEntityType.Furniture) {
        const furnitureData = cloneDeep(modelInfo.data);
        furnitureData.id = getUniqueId();
        this._modelStore.addFurnitureData(furnitureData);
      } else if (modelInfo.type === EEntityType.Assembly) {
        const assemblyData = cloneDeep(modelInfo.data);
        assemblyData.id = getUniqueId();

        const accessorieIds: string[] = [];
        for (const subModeldata of modelInfo.subModeldatas) {
          const furnitureData = cloneDeep(subModeldata);
          furnitureData.id = getUniqueId();
          this._modelStore.addFurnitureData(furnitureData);
          accessorieIds.push(furnitureData.id);
        }
        assemblyData.accessorieIds = accessorieIds;
        this._assemblyStore.addAssemblyData(assemblyData);
      }
    }

    this._history.completeOperation(EOperationType.MultipleEntityAdd);
  }

  private $history_deleteMultiModel(modelInfoList: IModelInfo[]) {
    const furnitureIds = new Set<string>();
    const assemblyIds = new Set<string>();
    const separateWallDatas: SeparateWallData[] = [];

    for (const modelInfo of modelInfoList) {
      if (modelInfo.type === EEntityType.Furniture) {
        furnitureIds.add(modelInfo.id);
      } else if (modelInfo.type === EEntityType.Assembly) {
        assemblyIds.add(modelInfo.id);
      } else if (modelInfo.type === EEntityType.SeparateWall) {
        // 只有绘制的隔墙可多选，房间轮廓的基础隔墙不在其中
        const separateWallData = this._separateWallStore.getSepData(modelInfo.id, false);
        if (separateWallData) separateWallDatas.push(separateWallData);
      }
    }

    if (furnitureIds.size === 0 && assemblyIds.size === 0 && separateWallDatas.length === 0) {
      return;
    }

    this._history.startOperation();

    const assemblyAccessoryIds = new Set<string>();

    for (const assemblyId of assemblyIds) {
      const assemblyData = this._assemblyStore.getAssemblyData(assemblyId);
      if (!assemblyData) {
        continue;
      }

      this._assemblyStore.removeAssemblyDataById(assemblyId);

      for (const furnitureId of assemblyData.accessorieIds) {
        assemblyAccessoryIds.add(furnitureId);
        if (this._modelStore.getFurnitureData(furnitureId)) {
          this._modelStore.removeFurnitureData(furnitureId);
        }
      }
    }

    for (const furnitureId of furnitureIds) {
      if (assemblyAccessoryIds.has(furnitureId)) {
        continue;
      }

      if (this._modelStore.getFurnitureData(furnitureId)) {
        this._modelStore.removeFurnitureData(furnitureId);
      }
    }

    // 隔墙的删除语义（清关联门窗、按房间刷新）只在 SeparateWallOperation 里有一份，这里复用它，
    // 但不能走它自带 history 事务的入口，否则会拆成两条撤销记录
    if (separateWallDatas.length) {
      globalStores.separateWallOperation.applyRemoveSeparateWalls(separateWallDatas);
    }

    this._history.completeOperation(EOperationType.MultipleEntityModify);
  }

  private $history_deleteMultiDoorWindow(modelInfoList: IModelInfo[]) {
    const windowIds = new Set<string>();

    for (const modelInfo of modelInfoList) {
      if (modelInfo.type === EEntityType.DoorWindow) {
        windowIds.add(modelInfo.id);
      }
    }

    if (windowIds.size === 0) {
      return;
    }

    this._history.startOperation();

    for (const windowId of windowIds) {
      if (this._modelStore.getWindowData(windowId)) {
        this._modelStore.removeWindowData(windowId);
      }
    }

    this._history.completeOperation(EOperationType.WindowDoorDel);
  }

  private $history_modifyMultiDoorWindow(doorWindowDatas: { id: string; data: Partial<IWindowData> }[]) {
    this._history.startOperation();

    for (const { id: windowId, data: windowData } of doorWindowDatas) {
      this._modelStore.updateWindowData(windowId, windowData);
    }

    this._history.completeOperation(EOperationType.WindowDoorModify);
  }

  transformModelInfos(selecteds: ISelected[], previous: IModelInfo[] = []) {
    const modelInfos: IModelInfo[] = cloneDeep(previous);

    for (const selected of selecteds) {
      const previousIndex = modelInfos.findIndex((modelInfo) => modelInfo.id === selected.entityId);
      // 已存在则移除，用于框选排除已选中和点选取消
      if (previousIndex !== -1) {
        modelInfos.splice(previousIndex, 1);
        continue;
      }

      switch (selected.type) {
        case EEntityType.Furniture: {
          const furnitureData = this._modelStore.getFurnitureData(selected.entityId);
          if (furnitureData) {
            const cloneFurnitureData = cloneDeep(furnitureData);

            modelInfos.push({
              id: cloneFurnitureData.id,
              type: EEntityType.Furniture,
              data: cloneFurnitureData,
              subModeldatas: []
            });
          }
          break;
        }
        case EEntityType.Assembly: {
          const assemblyData = this._assemblyStore.getAssemblyData(selected.entityId);
          if (assemblyData) {
            const cloneAssemblyData = cloneDeep(assemblyData);
            const furnitureDatas: IFurniture[] = [];
            for (const accessorieId of cloneAssemblyData.accessorieIds) {
              const furnitureData = this._modelStore.getFurnitureData(accessorieId);
              if (furnitureData) {
                const cloneFurnitureData = cloneDeep(furnitureData);
                furnitureDatas.push(cloneFurnitureData);
              }
            }

            modelInfos.push({
              id: cloneAssemblyData.id,
              type: EEntityType.Assembly,
              data: cloneAssemblyData,
              subModeldatas: furnitureDatas
            });
          }
          break;
        }
        case EEntityType.DoorWindow: {
          const doorWindowData = this._modelStore.getWindowData(selected.entityId);
          if (doorWindowData) {
            const cloneDoorWindowData = cloneDeep(doorWindowData);
            modelInfos.push({
              id: cloneDoorWindowData.id,
              type: EEntityType.DoorWindow,
              data: cloneDoorWindowData,
              subModeldatas: []
            });
          }
          break;
        }
        case EEntityType.SeparateWall: {
          // 只取绘制的隔墙，房间轮廓的基础隔墙不可多选
          const separateWallData = this._separateWallStore.getSepData(selected.entityId, false);
          if (separateWallData) {
            const cloneSeparateWallData = cloneDeep(separateWallData);
            modelInfos.push({
              id: cloneSeparateWallData.id,
              type: EEntityType.SeparateWall,
              data: cloneSeparateWallData,
              subModeldatas: []
            });
          }
          break;
        }
      }
    }

    return modelInfos;
  }

  private _getMoveModelInfos(moveOffset: IPoint2d, modelInfos: IBoxModelInfo[]) {
    const moveModeInfos: IModelInfo[] = [];
    for (const modelInfo of modelInfos) {
      const moveModelInfo = cloneDeep(modelInfo);
      moveModelInfo.data.center = Point2dUtils.added(moveModelInfo.data.center, moveOffset);
      for (const data of moveModelInfo.subModeldatas) {
        data.center = Point2dUtils.added(data.center, moveOffset);
      }
      moveModeInfos.push(moveModelInfo);
    }

    return moveModeInfos;
  }

  modifyMultiModel(modelInfos: IModelInfo[]) {
    this.$history_modifyMultiModel(modelInfos);
    modelManager.assemblyManager.update();
    modelManager.furnitureManager.update();
  }

  moveMultiModel(moveOffset: IPoint2d, modelInfos: IModelInfo[]) {
    // 含隔墙时不允许移动
    const boxModelInfos = toBoxModelInfos(modelInfos);
    if (!boxModelInfos) return;

    const moveModelInfos = this._getMoveModelInfos(moveOffset, boxModelInfos);
    this.$history_modifyMultiModel(moveModelInfos);
    modelManager.assemblyManager.update();
    modelManager.furnitureManager.update();
    modelManager.selectedRerender();
  }

  copyMultiModelData(modelInfos: IModelInfo[]) {
    this.$history_copyMultiModel(modelInfos);
    modelManager.furnitureManager.update();
    modelManager.assemblyManager.update();
  }

  copyMultiModel() {
    globalStores.editorStateStore.virtualMultiModelData = cloneDeep(
      globalStores.getDataByIdType('', EEntityType.MultiModel)
    );

    globalStores.operateInfo = {
      mode: EOperateMode.DrawMultiModel,
      toolType: EToolType.model,
      extraState: {
        copying: true
      }
    };

    this._editorStateStore.clearSelected();
  }

  deleteMultiModel(modelInfoList: IModelInfo[]) {
    const hasSeparateWall = modelInfoList.some((modelInfo) => modelInfo.type === EEntityType.SeparateWall);

    this.$history_deleteMultiModel(modelInfoList);

    if (hasSeparateWall) {
      // 隔墙牵动房间/分区/面，只更新家具那两个管理器不够
      modelManager.updateEntity();
    } else {
      modelManager.assemblyManager.update();
      modelManager.furnitureManager.update();
    }

    modelManager.selectedRerender();
  }

  deleteMultiDoorWindow(modelInfoList: IModelInfo[]) {
    this.$history_deleteMultiDoorWindow(modelInfoList);
    modelManager.windowManager.update();
    globalStores.faceStore.refreshFaceDatas();
    modelManager.faceManager.update();
    modelManager.selectedRerender();
  }

  modifyMultiDoorWindow(modelInfoList: IModelInfo[], behaviors: EParamsKeys[], value: number) {
    const needUpdateDoorWindows: { id: string; data: Partial<IWindowData> }[] = [];

    for (const modelInfo of modelInfoList) {
      if (modelInfo.type !== EEntityType.DoorWindow) {
        continue;
      }

      const { originalData, modifyData } = getModifyDoorWindowData(modelInfo.id, behaviors, value) ?? {};
      if (originalData && modifyData && !isEqual(originalData, modifyData)) {
        needUpdateDoorWindows.push({ id: originalData.id, data: modifyData });
      }
    }

    if (!needUpdateDoorWindows.length) {
      return;
    }

    this.$history_modifyMultiDoorWindow(needUpdateDoorWindows);

    const updatedDoorWindowIds = new Set(needUpdateDoorWindows.map(({ id }) => id));
    const nextModelInfos = modelInfoList.map((modelInfo) => {
      if (modelInfo.type !== EEntityType.DoorWindow || !updatedDoorWindowIds.has(modelInfo.id)) {
        return modelInfo;
      }

      const doorWindowData = this._modelStore.getWindowData(modelInfo.id);
      if (!doorWindowData) {
        return modelInfo;
      }

      return {
        ...modelInfo,
        data: cloneDeep(doorWindowData),
        subModeldatas: []
      };
    });

    this._updateMultiModelData({
      modelInfos: nextModelInfos,
      ...getMultiModelBoxInfo(nextModelInfos)
    });

    for (const { id } of needUpdateDoorWindows) {
      modelManager.windowManager.updateEntity(id);
    }
    globalStores.faceStore.refreshFaceDatas();
    modelManager.faceManager.update();
    modelManager.selectedRerender();
  }

  rotateMultiModel(modelInfo: IModelInfo[], center: IPoint2d, rotationDelta: number) {
    this._history.startOperation();
    const nextModelInfos: IModelInfo[] = [];
    for (const info of modelInfo) {
      switch (info.type) {
        case EEntityType.Furniture: {
          const furnitureData = this._modelStore.getFurnitureData(info.id);
          if (!furnitureData) continue;
          const nextData = cloneDeep(furnitureData);
          nextData.rotation = normalizeAngle(nextData.rotation + rotationDelta);
          nextData.center = Point2dUtils.rotatePoint(nextData.center, center, rotationDelta);
          this._modelStore.updateFurnitureData(info.id, nextData);
          nextModelInfos.push({
            id: info.id,
            type: EEntityType.Furniture,
            data: nextData,
            subModeldatas: []
          });
          break;
        }
        case EEntityType.Assembly: {
          const assemblyData = this._assemblyStore.getAssemblyData(info.id);
          if (!assemblyData) continue;
          const nextData = cloneDeep(assemblyData);
          nextData.rotation = normalizeAngle(nextData.rotation + rotationDelta);
          nextData.center = Point2dUtils.rotatePoint(nextData.center, center, rotationDelta);
          this._assemblyStore.updateAssemblyData(info.id, nextData);

          const nextSubModelDatas: IFurniture[] = [];
          for (const subModelData of info.subModeldatas) {
            const furnitureData = this._modelStore.getFurnitureData(subModelData.id);
            if (!furnitureData) continue;
            const nextFurnitureData = cloneDeep(furnitureData);
            nextFurnitureData.rotation = normalizeAngle(nextFurnitureData.rotation + rotationDelta);
            nextFurnitureData.center = Point2dUtils.rotatePoint(nextFurnitureData.center, center, rotationDelta);
            this._modelStore.updateFurnitureData(nextFurnitureData.id, nextFurnitureData);
            nextSubModelDatas.push(nextFurnitureData);
          }
          nextModelInfos.push({
            id: info.id,
            type: EEntityType.Assembly,
            data: nextData,
            subModeldatas: nextSubModelDatas
          });
        }
      }
    }
    this._history.completeOperation(EOperationType.MultipleEntityModify);
    this._history.mergeLastTwoOperationByType(EOperationType.MultipleEntityModify);
    modelManager.assemblyManager.update();
    modelManager.furnitureManager.update();
    this._updateMultiModelData({ modelInfos: nextModelInfos, rotationDelta });
  }

  /** 在修改数据后要刷新 select 临时数据 */
  private _updateMultiModelData(
    nextMultiModelData: Partial<
      Pick<IMultiModelData, 'modelInfos' | 'center' | 'rotation' | 'offGround' | 'width' | 'thickness' | 'height'> & {
        centerDelta: IPoint2d;
        rotationDelta: number;
      }
    >
  ) {
    const { modelInfos, centerDelta, rotationDelta, center, rotation, offGround, width, thickness, height } =
      nextMultiModelData;
    const multiModelData = globalStores.getDataByIdType('', EEntityType.MultiModel);
    if (multiModelData) {
      const updatedMultiModelData = {
        ...multiModelData,
        modelInfos: modelInfos ?? multiModelData.modelInfos,
        center:
          center ?? (centerDelta ? Point2dUtils.added(multiModelData.center, centerDelta) : multiModelData.center),
        rotation:
          rotation ?? (rotationDelta !== undefined ? multiModelData.rotation + rotationDelta : multiModelData.rotation),
        offGround: offGround ?? multiModelData.offGround,
        width: width ?? multiModelData.width,
        thickness: thickness ?? multiModelData.thickness,
        height: height ?? multiModelData.height
      };
      this._editorStateStore.setSelected(
        {
          entityId: MULTI_SELECT_TEMPORARY_ID,
          type: EEntityType.MultiModel
        },
        {
          multiModelData: updatedMultiModelData
        }
      );
    }
  }
}
